import React, { useMemo, useState, useEffect } from 'react';
import { Box, Button, Grid, Typography, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Business as BusinessIcon, CheckCircle as CheckIcon, Language as LanguageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TagInput from './TagInput';
import type { BusinessProfileFormData } from '../../types/business';
import { useBusinessInfoGenerator } from '../../hooks/useBusinessInfoGenerator';
import { useUsage, useLanguages } from '../../hooks/useCommon';
import { useAuth } from '../../hooks/useAuth';
import { useUpdateProfile } from '../../hooks/useAuth';
import BusinessInfoGeneratorSection from '../../components/features/business-profile/BusinessInfoGeneratorSection';
import BusinessInfoConfirmationDialog from '../../components/features/business-profile/BusinessInfoConfirmationDialog';
import QuotaConfirmationDialog from '../../components/features/business-profile/QuotaConfirmationDialog';
import AddressFieldsSection from '../../components/features/business-profile/AddressFieldsSection';
import BasicInfoFields from '../../components/features/business-profile/BasicInfoFields';

// Flag component using flagcdn.com
const Flag = ({ countryCode, size = 20 }: { countryCode: string; size?: number }) => (
  <img
    src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png 2x`}
    width={size}
    height={Math.round(size * 0.75)}
    alt={countryCode}
    style={{ borderRadius: 2, objectFit: 'cover' }}
  />
);

// Map language codes to country codes for flags
const getFlagCode = (languageCode: string): string => {
  const flagMap: { [key: string]: string } = {
    'en': 'GB', // English -> Great Britain
    'es': 'ES', // Spanish -> Spain
    'fr': 'FR', // French -> France
    'de': 'DE', // German -> Germany
    'zh': 'CN', // Chinese -> China
    'ar': 'SA', // Arabic -> Saudi Arabia
    'hi': 'IN', // Hindi -> India
  };
  return flagMap[languageCode] || 'UN';
};

interface BasicInfoStepProps {
  formData: BusinessProfileFormData;
  setFormData: (data: any) => void;
  tagInputs: any;
  setTagInputs: (inputs: any) => void;
  addressInput: string;
  setAddressInput: (input: string) => void;
  businessTypes: any[];
  loadingBusinessTypes: boolean;
  onNext: () => void;
  onTagAdd: (field: string) => void;
  onTagRemove: (field: string, index: number) => void;
  onTagInputChange: (field: string, value: string) => void;
  onTagKeyPress: (field: string, event: React.KeyboardEvent) => void;
  onAddressSelect: (place: any) => void;
  onGenerateBusinessInfo?: (url: string) => Promise<{ description_suggestion: string; products_services_suggestion: string; raw_context: string }>;
  profileId?: number | null;
  isValid: boolean;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  formData,
  setFormData,
  tagInputs,
  addressInput,
  setAddressInput,
  businessTypes,
  loadingBusinessTypes,
  onNext,
  onTagAdd,
  onTagRemove,
  onTagInputChange,
  onTagKeyPress,
  onAddressSelect,
  onGenerateBusinessInfo,
  profileId,
  isValid,
}) => {
  const { t } = useTranslation('profile');
  const { user } = useAuth();
  const { data: languages } = useLanguages();
  const updateProfile = useUpdateProfile();
  
  // State for content generation language (stored in user profile, not business profile)
  // Default to user's UI language if no content_generation_language is set
  const [contentGenerationLanguage, setContentGenerationLanguage] = useState<string>(
    user?.content_generation_language || user?.preferred_language || ''
  );
  const [hasSetDefault, setHasSetDefault] = useState(false);

  // Load user's current content generation language preference, or set default to UI language
  useEffect(() => {
    if (user?.content_generation_language) {
      setContentGenerationLanguage(user.content_generation_language);
      setHasSetDefault(true);
    } else if (user?.preferred_language && !hasSetDefault && languages && languages.length > 0) {
      // If no content_generation_language is set, default to UI language and save it
      const defaultLang = user.preferred_language;
      // Check if the preferred_language is in the available languages
      const isValidLang = languages.some((l: any) => l.code === defaultLang);
      if (isValidLang) {
        setContentGenerationLanguage(defaultLang);
        setHasSetDefault(true);
        // Save the default to DB so it's never empty
        updateProfile.mutateAsync({
          content_generation_language: defaultLang
        }).catch(err => {
          // console.error('Failed to set default content generation language:', err);
        });
      }
    }
  }, [user?.content_generation_language, user?.preferred_language, languages, hasSetDefault]);
  
  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle language change - save to user profile
  const handleLanguageChange = async (event: any) => {
    const newLanguage = event.target.value;
    setContentGenerationLanguage(newLanguage);
    
    // Save to user profile
    try {
      await updateProfile.mutateAsync({
        content_generation_language: newLanguage || undefined
      });
    } catch (error) {
      // console.error('Failed to save content generation language:', error);
      // Revert on error
      setContentGenerationLanguage(user?.content_generation_language || '');
    }
  };

  const handleAddressSelect = (place: any) => {
    // console.log('📍 BasicInfoStep: Calling onAddressSelect with place:', place);
    onAddressSelect(place);
  };

  // Get usage/quota data
  const { data: usage } = useUsage();

  // Calculate quota availability - use profile_scraping fields for business info generation
  const quotaInfo = useMemo(() => {
    if (!usage) {
      return { isAvailable: true, isDisabled: false, resetMessage: null };
    }

    // Check if profile scraping quota fields exist in usage response
    // If they don't exist (no profile yet, or backend didn't return them), assume quota is available
    const hasProfileScrapingQuotaInfo = usage.profile_scraping_monthly_limit !== undefined;
    if (!hasProfileScrapingQuotaInfo) {
      return { isAvailable: true, isDisabled: false, resetMessage: null };
    }

    // Use monthly fields if available, otherwise fall back to non-prefixed fields
    const monthlyRemaining = usage.profile_scraping_monthly_remaining ?? usage.profile_scraping_remaining ?? 0;
    const monthlyLimit = usage.profile_scraping_monthly_limit ?? usage.profile_scraping_limit;
    const monthlyUsed = usage.profile_scraping_monthly_used ?? usage.profile_scraping_used ?? 0;
    const isUnlimited = usage.profile_scraping_unlimited ?? false;
    
    // Use daily_remaining directly from API if available, otherwise calculate
    const dailyRemaining = usage.profile_scraping_daily_remaining !== undefined 
      ? usage.profile_scraping_daily_remaining 
      : (usage.profile_scraping_daily_limit !== undefined 
          ? Math.max(0, (usage.profile_scraping_daily_limit || 0) - (usage.profile_scraping_daily_used ?? 0))
          : undefined);
    const dailyLimit = usage.profile_scraping_daily_limit;
    const dailyUsed = usage.profile_scraping_daily_used ?? 0;

    // Check if quota is available
    const monthlyAvailable = isUnlimited || monthlyRemaining > 0;
    const dailyAvailable = dailyLimit === undefined || dailyRemaining === undefined || dailyRemaining > 0;
    const isAvailable = monthlyAvailable && dailyAvailable;

    // Determine reset message if quota is insufficient
    let resetMessage: string | null = null;
    if (!isAvailable) {
      const dailyExhausted = dailyLimit !== undefined && dailyRemaining !== undefined && dailyRemaining === 0;
      const monthlyExhausted = !isUnlimited && monthlyLimit !== undefined && monthlyRemaining === 0;
      
      if (monthlyExhausted || (dailyExhausted && monthlyExhausted)) {
        resetMessage = t('businessProfile.quota.featureAgainNextMonth');
      } else if (dailyExhausted) {
        resetMessage = t('businessProfile.quota.featureAgainTomorrow');
      }
    }

    return {
      isAvailable,
      isDisabled: !isAvailable,
      resetMessage,
    };
  }, [usage]);

  // Use business info generator hook
  const {
    generating,
    generationError,
    confirmDialogOpen,
    generationResult,
    quotaDialogOpen,
    pendingUrl,
    handleGenerateBusinessInfo,
    handleQuotaConfirm,
    handleQuotaCancel,
    handleConfirmReplace,
    handleCancelReplace,
  } = useBusinessInfoGenerator({
    formData,
    setFormData,
    onGenerateBusinessInfo,
    usage,
  });

  return (
    <Grid container spacing={3}>
      {/* Basic Info Fields - Now includes Website URL above Description */}
      <BasicInfoFields
        formData={{
          business_name: formData.business_name,
          business_type_id: formData.business_type_id,
          website_url: formData.website_url,
          description: formData.description,
        }}
        onChange={handleInputChange}
        businessTypes={businessTypes}
        loadingBusinessTypes={loadingBusinessTypes}
        isValid={isValid}
        websiteUrl={formData.website_url}
        onWebsiteChange={(value) => handleInputChange('website_url', value)}
        onGenerateBusinessInfo={handleGenerateBusinessInfo}
        generating={generating}
        generationError={generationError}
        disabled={quotaInfo.isDisabled}
        quotaMessage={quotaInfo.resetMessage}
      />

      {/* Address Section */}
      <Grid size={12}>
        <AddressFieldsSection
          addressInput={addressInput}
          onAddressInputChange={setAddressInput}
          onPlaceSelect={handleAddressSelect}
          formData={formData}
          onChange={handleInputChange}
          isValid={isValid}
        />
      </Grid>

      {/* Products/Services - Tags Section */}
      <Grid size={12}>
        <Box sx={{ 
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mb: 2.5,
              fontWeight: 600,
            }}
          >
            <BusinessIcon sx={{ color: 'primary.main' }} />
            {t('businessProfile.productsServices.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, fontSize: '0.875rem' }}>
            {t('businessProfile.productsServices.description')}
          </Typography>
          <TagInput
            field="products_services"
            label={t('businessProfile.productsServices.label')}
            color="info"
            tags={Array.isArray(formData.products_services) ? formData.products_services.filter(tag => tag && tag.trim()) : []}
            inputValue={tagInputs.products_services || ''}
            onInputChange={onTagInputChange}
            onTagAdd={onTagAdd}
            onTagRemove={onTagRemove}
            onKeyPress={onTagKeyPress}
            maxTags={10}
          />
        </Box>
      </Grid>

      {/* Content Generation Language Preference */}
      <Grid size={12}>
        <Box sx={{ 
          mt: 3,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mb: 1.5,
              fontWeight: 600,
            }}
          >
            <LanguageIcon sx={{ color: 'primary.main' }} />
            {t('businessProfile.contentGenerationLanguage.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, fontSize: '0.875rem' }}>
            {t('businessProfile.contentGenerationLanguage.description')}
          </Typography>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel>
              {t('businessProfile.contentGenerationLanguage.label')}
            </InputLabel>
            <Select
              value={contentGenerationLanguage}
              onChange={handleLanguageChange}
              label={t('businessProfile.contentGenerationLanguage.label')}
              disabled={updateProfile.isPending}
              renderValue={(value) => {
                const selectedLang = languages?.find((l: any) => l.code === value);
                if (!selectedLang) return value;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Flag countryCode={getFlagCode(selectedLang.code)} size={18} />
                    <span>{selectedLang.native_name || selectedLang.name}</span>
                  </Box>
                );
              }}
            >
              {languages?.map((lang: any) => (
                <MenuItem key={lang.code} value={lang.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Flag countryCode={getFlagCode(lang.code)} size={18} />
                    <span>{lang.native_name || lang.name}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>

      {/* Next Button */}
      <Grid size={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={onNext}
            disabled={!isValid}
            startIcon={<CheckIcon />}
            sx={{ minWidth: 200 }}
          >
            {t('businessProfile.buttons.nextContentStrategy')}
          </Button>
        </Box>
      </Grid>

      {/* Quota Confirmation Dialog */}
      <QuotaConfirmationDialog
        open={quotaDialogOpen}
        onClose={handleQuotaCancel}
        onConfirm={handleQuotaConfirm}
        usage={usage}
        generationType="business-info"
        websiteUrl={pendingUrl || undefined}
      />

      {/* Confirmation Dialog for Overwriting Existing Data */}
      <BusinessInfoConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleCancelReplace}
        onConfirm={handleConfirmReplace}
        currentDescription={formData.description}
        currentProducts={formData.products_services}
        newDescription={generationResult?.description_suggestion}
        newProducts={generationResult?.products_services_suggestion}
      />
    </Grid>
  );
};

export default BasicInfoStep;
